/**
 * Schemas Zod do perfil da clínica (PR-M2-01).
 *
 * **Escopo do form**: dados cadastrais editáveis pelo owner/admin.
 * Razão social e CNPJ NÃO entram (travados depois do provisionamento —
 * só superadmin altera via banco). Logo + cor do tema também NÃO entram
 * aqui — ficam pra PR-M2-04 (identidade visual com paletas curadas
 * tested em light+dark).
 *
 * Reusa helpers BR de `features/leads/formatters.ts` pra normalizar
 * (Title Case, formatação CEP/telefone) — manter um único padrão visual
 * em todo o app.
 *
 * Strings de erro são chaves i18n (`clinics.profile.validation.*`)
 * resolvidas no consumo via `useFieldError`.
 */
import { z } from 'zod';

import {
  formatCep,
  formatPhoneBR,
  normalizeCep,
  normalizePhoneBR,
  toTitleCase,
} from '../leads/formatters.js';

/** Endereço da clínica — armazenado como JSONB em `clinics.address`. */
export const clinicAddressSchema = z.object({
  cep: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v ? normalizeCep(v) : null))
    .refine(
      (v) => v == null || v === '' || v.length === 8,
      'clinics.profile.validation.cep.invalid',
    ),
  street: z.string().optional().nullable(),
  number: z.string().optional().nullable(),
  complement: z.string().optional().nullable(),
  district: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  uf: z
    .string()
    .optional()
    .nullable()
    .refine(
      (v) => v == null || v === '' || /^[A-Za-z]{2}$/.test(v),
      'clinics.profile.validation.uf.invalid',
    ),
});

export type ClinicAddressInput = z.infer<typeof clinicAddressSchema>;

/**
 * Schema do form `ClinicProfileForm`. Apenas campos editáveis:
 * - `trade_name` — nome fantasia (max 120 chars)
 * - `email` — email comercial (formato válido se preenchido)
 * - `phone` — telefone BR (10 ou 11 dígitos se preenchido)
 * - `address` — JSONB nested
 *
 * Razão social e CNPJ aparecem na UI como **read-only** (não passam
 * por aqui) — Karla altera via banco se cliente pedir mudança formal.
 */
export const clinicProfileSchema = z.object({
  trade_name: z
    .string()
    .max(120, 'clinics.profile.validation.tradeName.tooLong')
    .optional()
    .nullable(),
  email: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v ? v.trim().toLowerCase() : null))
    .refine(
      (v) => v == null || v === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
      'clinics.profile.validation.email.invalid',
    ),
  phone: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v ? normalizePhoneBR(v) : null))
    .refine(
      (v) => v == null || v === '' || (v.length >= 10 && v.length <= 11),
      'clinics.profile.validation.phone.invalid',
    ),
  address: clinicAddressSchema,
});

export type ClinicProfileInput = z.infer<typeof clinicProfileSchema>;

/**
 * Aplica padronização visual do KmGestao em todos os campos textuais
 * antes de persistir (decisão de produto 2026-04 — manter um único
 * padrão visual). Regras:
 *   - `trade_name`: Title Case ("clinica norte" → "Clínica Norte")
 *   - `email`: lowercase + trim (já feito no schema)
 *   - `phone`: formato canônico (XX) XXXXX-XXXX
 *   - Endereço (street/district/city/complement): Title Case
 *   - UF: UPPERCASE
 *   - CEP: formato XXXXX-XXX
 *
 * Use antes de chamar `useUpdateClinicProfile`. Espelha
 * `normalizeLeadInput` em `features/leads/formatters.ts`.
 */
export function normalizeClinicProfileInput(input: ClinicProfileInput): ClinicProfileInput {
  return {
    trade_name: input.trade_name ? toTitleCase(input.trade_name) : null,
    email: input.email,
    phone: input.phone ? formatPhoneBR(input.phone) : null,
    address: {
      cep: input.address.cep ? formatCep(input.address.cep) : null,
      street: input.address.street ? toTitleCase(input.address.street) : null,
      number: input.address.number?.trim() ?? null,
      complement: input.address.complement ? toTitleCase(input.address.complement) : null,
      district: input.address.district ? toTitleCase(input.address.district) : null,
      city: input.address.city ? toTitleCase(input.address.city) : null,
      uf: input.address.uf ? input.address.uf.trim().toUpperCase().slice(0, 2) : null,
    },
  };
}
